<template>
    <div>
        <!-- Filters -->
        <v-row align="center" class="mb-4" no-gutters>
            <v-col cols="auto" class="mr-3">
                <v-select v-model="based_by" :items="based_by_options" label="Show" outlined dense hide-details
                    style="max-width:220px;" @change="onBasedByChange"></v-select>
            </v-col>

            <template v-if="based_by">
                <v-col cols="auto" class="mr-2">
                    <v-menu v-model="from_menu" :close-on-content-click="false" transition="scale-transition" offset-y
                        min-width="290px">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="date_from" label="From" readonly outlined dense hide-details
                                prepend-inner-icon="mdi-calendar" style="max-width:175px;" v-bind="attrs" v-on="on">
                            </v-text-field>
                        </template>
                        <v-date-picker v-model="date_from" no-title :max="date_to"
                            @input="from_menu = false; fetchReport()"></v-date-picker>
                    </v-menu>
                </v-col>
                <v-col cols="auto" class="mr-3">
                    <v-menu v-model="to_menu" :close-on-content-click="false" transition="scale-transition" offset-y
                        min-width="290px">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="date_to" label="To" readonly outlined dense hide-details
                                prepend-inner-icon="mdi-calendar" style="max-width:175px;" v-bind="attrs" v-on="on">
                            </v-text-field>
                        </template>
                        <v-date-picker v-model="date_to" no-title :min="date_from"
                            @input="to_menu = false; fetchReport()"></v-date-picker>
                    </v-menu>
                </v-col>
            </template>

            <v-col cols="auto" class="mr-3">
                <v-switch v-model="include_checked" label="Include marked OK" dense hide-details class="mt-0"
                    color="#3988AA" @change="fetchReport()"></v-switch>
            </v-col>

            <v-col cols="auto">
                <v-progress-circular v-if="loading" indeterminate size="24" color="#3988AA"></v-progress-circular>
            </v-col>
        </v-row>

        <v-card outlined class="card-format pa-4">
            <div class="chart-title">Low Profit Packages</div>
            <div class="chart-subtitle mb-4">
                Packages booked to live events whose profit per person (total less the suppliers' net prices, or the
                final rates once staff change them) is below the margin set for their location in Settings &gt; Profit
                Margins, or below the company's Minimum Quote Profit where the location has no margin.
            </div>

            <v-data-table :headers="headers" :items="rows" :loading="loading" disable-pagination hide-default-footer
                item-key="quote_id" no-data-text="No low profit packages to show" style="font-family: Manrope;">
                <template v-slot:item="{ item }">
                    <tr>
                        <td>
                            <router-link :to="{ name: 'ManageEvent', params: { event_id: item.event_id } }">
                                {{ item.event_name || 'Event #' + item.event_id }}
                            </router-link>
                            <div class="sub-text">{{ [item.location, item.event_type].filter(Boolean).join(' ') }}</div>
                        </td>
                        <td>{{ item.event_date ? $date(item.event_date).format('DD.MM.YY') : '' }}</td>
                        <td>
                            <router-link :to="{ name: 'EditCustomQuote', params: { lead_id: item.lead_id, quote_id: item.quote_id } }">
                                {{ item.quote_name }}
                            </router-link>
                            <div class="sub-text" v-if="item.date_sent">Sent {{ $date(item.date_sent).format('DD.MM.YY') }}</div>
                        </td>
                        <td class="text-right">{{ money(item.total) }}</td>
                        <td class="text-right">{{ money(item.cost) }}</td>
                        <td class="text-right">
                            <span class="low-profit">{{ money(item.profit) }}</span>
                        </td>
                        <td class="text-right">
                            {{ money(item.minimum) }}
                            <div class="sub-text" v-if="item.minimum_source === 'company'">Company minimum</div>
                            <div class="sub-text" v-else>{{ item.margin_type === 'accommodation' ? 'Accommodation' : 'Activity only' }}</div>
                        </td>
                        <td class="text-right low-profit">{{ money(item.shortfall) }}</td>
                        <td>{{ discountText(item) }}</td>
                        <td class="text-right" style="white-space:nowrap;">
                            <v-chip v-if="item.checked_at" x-small color="success" class="mr-1"
                                :title="item.checked_by_name ? 'Marked OK by ' + item.checked_by_name : 'Marked OK'">
                                OK {{ $date(item.checked_at).format('DD.MM.YY') }}
                            </v-chip>
                            <v-btn v-else small depressed dark color="#679F54" class="mr-1" :loading="marking === item.quote_id"
                                @click="markChecked(item)" title="Take it off the report until its profit changes again">
                                <v-icon small left>mdi-check</v-icon>Mark OK
                            </v-btn>
                            <v-btn small icon @click="openLogs(item)" title="Low profit log">
                                <v-icon small>mdi-history</v-icon>
                            </v-btn>
                        </td>
                    </tr>
                </template>
            </v-data-table>
        </v-card>

        <!-- Per-package log -->
        <v-dialog v-model="logs_dialog" max-width="800" scrollable>
            <v-card>
                <v-card-title class="chart-title">Low profit log — {{ logs_quote ? logs_quote.quote_name : '' }}</v-card-title>
                <v-card-text>
                    <v-data-table :headers="log_headers" :items="logs" :loading="logs_loading" disable-pagination
                        hide-default-footer no-data-text="Nothing logged yet" style="font-family: Manrope;">
                        <template v-slot:item="{ item }">
                            <tr>
                                <td>{{ $date(item.created_at).format('DD.MM.YY HH:mm') }}</td>
                                <td>{{ item.user ? item.user.name : '' }}</td>
                                <td>{{ item.action }}</td>
                                <td class="text-right">{{ item.old_profit === null ? '' : money(item.old_profit) }}</td>
                                <td class="text-right">{{ item.new_profit === null ? '' : money(item.new_profit) }}</td>
                                <td class="text-right">{{ item.minimum === null ? '' : money(item.minimum) }}</td>
                            </tr>
                        </template>
                    </v-data-table>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="logs_dialog = false">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    data: () => ({
        loading: false,
        rows: [],
        based_by: null,
        based_by_options: [
            { text: 'Upcoming events', value: null },
            { text: 'By event date', value: 'event_date' },
            { text: 'By date quote sent', value: 'date_sent' },
        ],
        date_from: null,
        date_to: null,
        from_menu: false,
        to_menu: false,
        include_checked: false,
        marking: null,
        headers: [
            { text: 'Event', sortable: false },
            { text: 'Event Date', sortable: false },
            { text: 'Package', sortable: false },
            { text: 'Total', sortable: false, align: 'right' },
            { text: 'Cost', sortable: false, align: 'right' },
            { text: 'Profit', sortable: false, align: 'right' },
            { text: 'Minimum', sortable: false, align: 'right' },
            { text: 'Short By', sortable: false, align: 'right' },
            { text: 'Discount', sortable: false },
            { text: '', sortable: false },
        ],
        logs_dialog: false,
        logs_loading: false,
        logs_quote: null,
        logs: [],
        log_headers: [
            { text: 'When', sortable: false },
            { text: 'By', sortable: false },
            { text: 'What', sortable: false },
            { text: 'Old Profit', sortable: false, align: 'right' },
            { text: 'New Profit', sortable: false, align: 'right' },
            { text: 'Minimum', sortable: false, align: 'right' },
        ],
    }),
    async mounted() {
        await this.fetchReport()
    },
    computed: {
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
        }),
    },
    methods: {
        money(value) {
            return this.get_selected_company.currency + Number(value || 0).toFixed(2)
        },
        discountText(item) {
            if (!item.discount || Number(item.discount) === 0) return ''
            return item.discount_type === 'percent' ? `${item.discount}%` : this.money(item.discount)
        },
        onBasedByChange() {
            if (this.based_by && (!this.date_from || !this.date_to)) {
                const today = new Date()
                const pad = n => String(n).padStart(2, '0')
                const str = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
                const later = new Date(today)
                later.setDate(today.getDate() + 30)
                this.date_from = str(today)
                this.date_to = str(later)
            }
            this.fetchReport()
        },
        async fetchReport() {
            this.loading = true
            try {
                const params = { include_checked: this.include_checked ? 1 : 0 }
                if (this.based_by && this.date_from && this.date_to) {
                    params.based_by = this.based_by
                    params.from = this.date_from
                    params.to = this.date_to
                }
                const { data } = await this.$axios.get(`admin/quotes/low_profit_report/${this.get_selected_company.id}`, params)
                if (data && data.response) {
                    this.rows = data.data
                } else if (data) {
                    this.$toast.error(data.message || 'Could not load the low profit report')
                }
            } finally {
                this.loading = false
            }
        },
        async markChecked(item) {
            this.marking = item.quote_id
            try {
                const { data } = await this.$axios.post(`admin/quotes/mark_low_profit_checked/${item.quote_id}`, {})
                if (data && data.response) {
                    this.$toast.success(data.message)
                    await this.fetchReport()
                } else {
                    this.$toast.error((data && data.message) || 'Could not mark it as OK')
                }
            } finally {
                this.marking = null
            }
        },
        async openLogs(item) {
            this.logs_quote = item
            this.logs = []
            this.logs_dialog = true
            this.logs_loading = true
            try {
                const { data } = await this.$axios.get(`admin/quotes/profit_logs/${item.quote_id}`)
                if (data && data.response) {
                    this.logs = data.data
                }
            } finally {
                this.logs_loading = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.card-format {
    border-radius: 15px !important;
}

.chart-title {
    font-family: 'Work Sans', sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #3C3C3C;
}

.chart-subtitle {
    font-family: 'Work Sans', sans-serif;
    font-size: 13px;
    color: #7C7C7C;
}

.sub-text {
    font-size: 12px;
    color: #7C7C7C;
}

.low-profit {
    color: #D32F2F;
    font-weight: 700;
}
</style>
